import React, { useState, useEffect } from "react";
import Select from "react-select";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { registerLocale } from "react-datepicker";
import ru from "date-fns/locale/ru";
import "./AddAgreement.css";
import { selectStyles } from "../../../../../utils/selectStyles";
import { getProjectEstimate } from "../../../../../api/projectEstimatesApi";
import {
  getUsersSummary,
  getAllCounterparties,
} from "../../../../../api/api";
import { addAgreement } from "../../../../../api/agreementsApi";
import AddCounterpartyModal from "../../../CounterpartiesDatabase/AddCounterpartyModal/AddCounterpartyModal";

registerLocale("ru", ru);

const CREATE_SUBCONTRACTOR_OPTION = {
  value: "__create_new_subcontractor__",
  label: "+ Создать субподрядчика",
};

const getCounterpartyLabel = (c) =>
  c?.full_name ||
  c?.full_company_name ||
  c?.short_company_name ||
  c?.short_name ||
  (c?.id != null ? `Контрагент #${c.id}` : "");

const performerTypes = [
  { value: "contract_employee", label: "Сотрудник на подряд" },
  { value: "individual_gph", label: "Физ лицо на ГПХ" },
  { value: "too_without_vat", label: "ТОО без НДС" },
  { value: "ip", label: "ИП" },
  { value: "too_with_vat", label: "ТОО с НДС" },
];

// Шаблоны графика оплат (пока один)
const PAYMENT_TEMPLATES = [
  { value: "30_50_20", label: "30/50/20 (стандартный)" },
];

const PAYMENT_TEMPLATE_30_50_20 = [
  {
    key: "advance",
    label: "Аванс",
    percent: 30,
    description: "Подписание договора между Генпроектировщиком и Исполнителем",
    terms: "Оплата в течение 10 банковских дней",
  },
  {
    key: "second",
    label: "Второй транш",
    percent: 50,
    description: "Заключение договора с РГП Госэкспертиза",
    terms: "Оплата в течение 10 банковских дней",
  },
  {
    key: "third",
    label: "Третий транш",
    percent: 20,
    description:
      "Отработка замечаний ДУП, коллизий и BIM, принятие Заказчиком проекта и подписание между сторонами (Заказчик и Генпроектировщик) Акта выполненных работ",
    terms: "Оплата в течение 10 банковских дней",
  },
];

const AddAgreement = ({ open, onClose, projectId }) => {
  const [currentStep, setCurrentStep] = useState(1);
  const [agreementNumber, setAgreementNumber] = useState("№0000001");
  const [agreementDate, setAgreementDate] = useState(() => {
    const date = new Date();
    date.setDate(1);
    date.setMonth(0);
    date.setFullYear(2025);
    return date;
  });
  const [performerType, setPerformerType] = useState("too_with_vat");
  const [selectedPerformer, setSelectedPerformer] = useState(null);
  const [performerOptionsList, setPerformerOptionsList] = useState([]);
  const [loadingPerformers, setLoadingPerformers] = useState(false);

  // Состояние для данных сметы
  const [estimateData, setEstimateData] = useState(null);
  const [sectionsData, setSectionsData] = useState({});
  const [loadingEstimate, setLoadingEstimate] = useState(false);
  const [selectedSections, setSelectedSections] = useState(new Set());
  const [selectedBlocks, setSelectedBlocks] = useState({}); // {sectionId: {blockId: {selected: bool, cost: number}}}
  const [agreementCosts, setAgreementCosts] = useState({}); // {sectionId: {blockId: cost}}

  // Шаг 3: сроки и порядок оплаты
  const [executionStartDate, setExecutionStartDate] = useState(() => new Date());
  const [executionEndDate, setExecutionEndDate] = useState(() => {
    const d = new Date();
    d.setDate(d.getDate() + 10);
    return d;
  });
  const [paymentTemplate, setPaymentTemplate] = useState("30_50_20");
  // План даты платежей по этапам (ключ — stage.key: advance, second, third)
  const [paymentPlanDates, setPaymentPlanDates] = useState({});
  const [savingAgreement, setSavingAgreement] = useState(false);
  const [saveError, setSaveError] = useState(null);

  const [showAddSubcontractorModal, setShowAddSubcontractorModal] = useState(false);
  const [extraSubcontractors, setExtraSubcontractors] = useState([]);

  const handleStepClick = (step) => {
    // Можно переходить к любому уже пройденному шагу или следующему
    if (step <= currentStep + 1) {
      setCurrentStep(step);
    }
  };

  // Загрузка опций исполнителя: «Сотрудник на подряд» — users-summary, остальные — контрагенты с фильтрами
  useEffect(() => {
    if (!open) return;

    const loadPerformerOptions = async () => {
      setLoadingPerformers(true);
      setPerformerOptionsList([]);
      try {
        if (performerType === "contract_employee") {
          const usersData = await getUsersSummary({
            isActive: true,
            pageSize: 500,
          });
          const users = usersData?.users || [];
          const options = users.map((user) => {
            const label = `${user.surname || user.lastName || ""} ${user.name || user.firstName || ""} ${user.patronymic || user.middleName || ""}`.trim() || `Пользователь #${user.id}`;
            return {
              value: user.id,
              label,
              data: { _source: "user", ...user },
            };
          });
          setPerformerOptionsList(options);
        } else {
          const list = await getAllCounterparties({
            counterparty_side: "counterparty",
          });
          const arr = Array.isArray(list) ? list : [];

          const filterByPerformerType = (item) => {
            if (performerType === "individual_gph")
              return item.type === "physical_entity" && item.legal_form === "Физическое лицо на ГПХ";
            if (performerType === "ip")
              return item.type === "physical_entity" && item.legal_form === "ИП";
            if (performerType === "too_without_vat")
              return item.type === "legal_entity" && !item.is_nds_payer;
            if (performerType === "too_with_vat")
              return item.type === "legal_entity" && item.is_nds_payer;
            return false;
          };

          const filtered = arr.filter(filterByPerformerType);
          const options = filtered.map((item) => {
            const label =
              item.full_name ||
              item.full_company_name ||
              item.short_company_name ||
              item.short_name ||
              `Контрагент #${item.id}`;
            return {
              value: item.id,
              label,
              data: { _source: "counterparty", ...item },
            };
          });
          setPerformerOptionsList(options);
        }
      } catch (err) {
        console.error("Ошибка загрузки списка исполнителей:", err);
        setPerformerOptionsList([]);
      } finally {
        setLoadingPerformers(false);
      }
    };

    loadPerformerOptions();
  }, [open, performerType]);

  // Загрузка данных сметы при переходе на второй шаг
  useEffect(() => {
    if (currentStep === 2 && projectId && !estimateData) {
      loadEstimateData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentStep, projectId]);

  const loadEstimateData = async () => {
    if (!projectId) return;
    setLoadingEstimate(true);
    try {
      const estimate = await getProjectEstimate(projectId);
      setEstimateData(estimate);

      const allSections = [
        ...(estimate.construction_design || []),
        ...(estimate.construction_design_project || []),
      ];

      // Детали секций из одного ответа (construction_design_detailed)
      const detailedBySectionId = {};
      (estimate.construction_design_detailed || []).forEach((det) => {
        if (det.section_id != null) {
          detailedBySectionId[det.section_id] = det;
        }
      });

      const sectionsDetails = {};
      for (const section of allSections) {
        const sid = section.section?.id;
        if (sid == null) continue;
        const detail = detailedBySectionId[sid];
        sectionsDetails[sid] = {
          ...section,
          block_expenditures: detail?.block_expenditures || [],
        };
      }
      setSectionsData(sectionsDetails);
    } catch (err) {
      console.error("Ошибка загрузки данных сметы:", err);
    } finally {
      setLoadingEstimate(false);
    }
  };

  if (!open) return null;

  const handlePerformerSelect = (option) => {
    setSelectedPerformer(option?.data || null);
  };

  const handleNext = () => {
    if (currentStep < 3) {
      setCurrentStep(currentStep + 1);
    }
  };

  const performerData = selectedPerformer;
  const isEmployeePerformer = performerData?._source === "user";
  const isCounterpartyPerformer = performerData?._source === "counterparty";

  const formatCounterpartyAddress = (c) => {
    if (!c) return "—";
    const parts = [c.country, c.postal_code, c.city, c.street, c.building].filter(Boolean);
    return parts.length ? parts.join(", ") : "—";
  };

  // Общая сумма соглашения со шага «Стоимость по блокам»
  const getTotalAgreementCost = () => {
    return Array.from(selectedSections).reduce(
      (sum, sectionId) => {
        const sectionCosts = agreementCosts[sectionId] || {};
        return (
          sum +
          Object.values(sectionCosts).reduce((s, cost) => s + (cost || 0), 0)
        );
      },
      0
    );
  };

  const totalAgreementCost = getTotalAgreementCost();

  // Наименование субподрядчика (выбран на шаге 1)
  const getSubcontractorName = () => {
    if (!selectedPerformer) return "—";
    if (selectedPerformer._source === "user") {
      return [selectedPerformer.surname || selectedPerformer.lastName, selectedPerformer.name || selectedPerformer.firstName, selectedPerformer.patronymic || selectedPerformer.middleName].filter(Boolean).join(" ") || "—";
    }
    return selectedPerformer.full_name || selectedPerformer.full_company_name || selectedPerformer.short_company_name || selectedPerformer.short_name || "—";
  };

  // Сторона генпроектировщика (берётся из выбора до этого; пока константа)
  const GENPROEKT_NAME = "ТОО «AQMOL-project» (Генпроектировщик)";

  // Предмет соглашения: разделы + объект (из сметы/проекта)
  const getAgreementSubject = () => {
    const sectionNames = Array.from(selectedSections)
      .map((sectionId) => sectionsData[sectionId]?.section?.name)
      .filter(Boolean);
    const namesStr = sectionNames.length ? sectionNames.join(" и ") : "выбранным разделам";
    const objectName = estimateData?.project?.name || estimateData?.name || "объекта";
    return `Выполнение проектных работ по разделам ${namesStr} для ${objectName}`;
  };

  // Рабочие дни между датами (без учёта праздников)
  const countWorkingDays = (start, end) => {
    if (!start || !end || end < start) return 0;
    let count = 0;
    const d = new Date(start);
    d.setHours(0, 0, 0, 0);
    const endD = new Date(end);
    endD.setHours(0, 0, 0, 0);
    while (d <= endD) {
      const day = d.getDay();
      if (day !== 0 && day !== 6) count += 1;
      d.setDate(d.getDate() + 1);
    }
    return count;
  };

  const formatDateRange = (start, end) => {
    if (!start || !end) return "—";
    const fmt = (d) =>
      d.toLocaleDateString("ru-RU", { day: "2-digit", month: "2-digit", year: "numeric" });
    const days = countWorkingDays(start, end);
    return days > 0 ? `${fmt(start)}–${fmt(end)} (${days} рабочих дней)` : `${fmt(start)}–${fmt(end)}`;
  };

  const selectedTemplateStages = paymentTemplate === "30_50_20" ? PAYMENT_TEMPLATE_30_50_20 : [];

  // Собираем ID блоков по выбранным разделам и блокам (из block_expenditures)
  const getBlockIdsForPayload = () => {
    const ids = [];
    Array.from(selectedSections).forEach((sectionId) => {
      const section = sectionsData[sectionId];
      const blocks = section?.block_expenditures || [];
      const sectionCosts = agreementCosts[sectionId] || {};
      Object.keys(sectionCosts).forEach((blockIndexStr) => {
        const blockIndex = parseInt(blockIndexStr, 10);
        if (Number.isNaN(blockIndex) || !sectionCosts[blockIndexStr]) return;
        const block = blocks[blockIndex];
        const id = block?.id ?? block?.block?.id;
        if (id != null) ids.push(Number(id));
      });
    });
    return ids;
  };

  const formatDateForApi = (d) => {
    if (!d) return null;
    const date = d instanceof Date ? d : new Date(d);
    return date.toISOString().slice(0, 10);
  };

  const handleSave = async () => {
    setSaveError(null);
    setSavingAgreement(true);
    try {
      const blockIds = getBlockIdsForPayload();
      const payload = {
        number: parseInt(String(agreementNumber).replace(/\D/g, ""), 10) || 0,
        status: "draft",
        date_from: formatDateForApi(executionStartDate),
        date_to: formatDateForApi(executionEndDate),
        performer_type: performerType,
        project_id: Number(projectId) || 0,
        price: Math.round(totalAgreementCost),
        smeta_version: estimateData?.version ?? 0,
        block_ids: blockIds,
      };
      if (selectedPerformer?._source === "counterparty") {
        payload.company_id = Number(selectedPerformer.id) || 0;
      }
      if (selectedPerformer?._source === "user") {
        // API принимает только company_id и counterparty_id; для «Сотрудник на подряд» передаём counterparty_id
        payload.counterparty_id = Number(selectedPerformer.id) || 0;
      }
      await addAgreement(payload);
      onClose();
    } catch (err) {
      setSaveError(err.message || "Не удалось сохранить соглашение");
    } finally {
      setSavingAgreement(false);
    }
  };

  const handleOverlayClick = (e) => {
    // Не закрывать при клике по вложенному модалу (например, «Добавить контрагента»)
    if (
      e.target.closest(".add-counterparty-modal") ||
      e.target.closest(".add-counterparty-modal-backdrop")
    ) {
      return;
    }
    onClose();
  };

  return (
    <div className="add-agreement-overlay" onClick={handleOverlayClick}>
      <div
        className="add-agreement-container"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="add-agreement-header">
          <h2 className="add-agreement-title">Создание соглашения</h2>
          <button className="add-agreement-close" onClick={onClose}>
            ×
          </button>
        </div>

        <div className="add-agreement-progress">
          <div
            className={`progress-step ${currentStep >= 1 ? "active" : ""} ${
              currentStep > 1 ? "completed" : ""
            } ${currentStep >= 1 ? "clickable" : ""}`}
            onClick={() => handleStepClick(1)}
          >
            <div className="progress-step-number">1</div>
            <div className="progress-step-label">Сведения о субподрядчике</div>
          </div>
          <div
            className={`progress-step ${currentStep >= 2 ? "active" : ""} ${
              currentStep > 2 ? "completed" : ""
            } ${currentStep >= 2 ? "clickable" : ""}`}
            onClick={() => handleStepClick(2)}
          >
            <div className="progress-step-number">2</div>
            <div className="progress-step-label">Состав работ и стоимость</div>
          </div>
          <div
            className={`progress-step ${currentStep >= 3 ? "active" : ""} ${
              currentStep >= 3 ? "clickable" : ""
            }`}
            onClick={() => handleStepClick(3)}
          >
            <div className="progress-step-number">3</div>
            <div className="progress-step-label">Сроки и порядок оплаты</div>
          </div>
        </div>

        {currentStep === 1 && (
          <div className="add-agreement-content">
            <div className="agreement-info-section">
              <h3 className="section-label">Соглашение</h3>
              <div className="agreement-info-fields">
                <div className="agreement-field">
                  <input
                    type="text"
                    value={agreementNumber}
                    onChange={(e) => setAgreementNumber(e.target.value)}
                    className="agreement-input"
                  />
                </div>
                <div className="agreement-field">
                  <DatePicker
                    selected={agreementDate}
                    onChange={(date) => setAgreementDate(date)}
                    dateFormat="dd.MM.yyyy"
                    locale="ru"
                    className="agreement-input agreement-datepicker"
                    popperClassName="add-agreement-datepicker-popper"
                    placeholderText="дд.мм.гггг"
                  />
                </div>
              </div>
            </div>

            <div className="subcontractor-info-section">
              <h3 className="section-title">Сведения о субподрядчике</h3>

              <div className="form-group">
                <div className="form-group-header">
                  <label className="form-label">Выбор субподрядчика</label>
                </div>
                <p className="form-description">
                  Выберите субподрядчика из справочника контрагентов
                </p>
              </div>

              <div className="form-group">
                <label className="form-label">Тип исполнителя</label>
                <div className="radio-group">
                  {performerTypes.map((type) => (
                    <label key={type.value} className="radio-label">
                      <input
                        type="radio"
                        name="performerType"
                        value={type.value}
                        checked={performerType === type.value}
                        onChange={(e) => {
                          setPerformerType(e.target.value);
                          setSelectedPerformer(null);
                        }}
                        className="radio-input"
                      />
                      <span className="radio-custom"></span>
                      <span className="radio-text">{type.label}</span>
                    </label>
                  ))}
                </div>
              </div>

              <div className="form-group">
                <label className="form-label">
                  Выберите исполнителя<span className="required">*</span>
                </label>
                {(() => {
                  const performerSelectOptions =
                    performerType === "contract_employee"
                      ? performerOptionsList
                      : [
                          CREATE_SUBCONTRACTOR_OPTION,
                          ...extraSubcontractors.map((c) => ({
                            value: c.id,
                            label: getCounterpartyLabel(c),
                            data: { _source: "counterparty", ...c },
                          })),
                          ...performerOptionsList,
                        ];
                  const selectedOption = selectedPerformer
                    ? performerSelectOptions.find(
                        (opt) => opt.value === selectedPerformer.id
                      ) ||
                      (selectedPerformer?.id != null
                        ? {
                            value: selectedPerformer.id,
                            label: getCounterpartyLabel(selectedPerformer),
                            data: selectedPerformer,
                          }
                        : null)
                    : null;
                  return (
                <Select
                  styles={selectStyles}
                  options={performerSelectOptions}
                  value={selectedOption}
                  onChange={(option) => {
                    if (
                      option?.value === CREATE_SUBCONTRACTOR_OPTION.value
                    ) {
                      setShowAddSubcontractorModal(true);
                      return;
                    }
                    handlePerformerSelect(option);
                  }}
                  isLoading={loadingPerformers}
                  placeholder="Выберите исполнителя..."
                  noOptionsMessage={() =>
                    loadingPerformers
                      ? "Загрузка..."
                      : "Нет доступных исполнителей"
                  }
                  formatOptionLabel={(option) =>
                    option?.value === CREATE_SUBCONTRACTOR_OPTION.value ? (
                      <span style={{ color: "#2a5e38", fontWeight: 500 }}>
                        {option.label}
                      </span>
                    ) : (
                      option?.label
                    )
                  }
                  menuPortalTarget={document.body}
                  menuPosition="fixed"
                />
                  );
                })()}
              </div>

              {performerData && (
                <div className="requisites-section">
                  <h4 className="requisites-title">Реквизиты:</h4>
                  <div className="requisites-grid">
                    {isEmployeePerformer ? (
                      <>
                        <div className="requisite-item">
                          <span className="requisite-label">ФИО:</span>
                          <span className="requisite-value">
                            {[
                              performerData.surname || performerData.lastName,
                              performerData.name || performerData.firstName,
                              performerData.patronymic || performerData.middleName,
                            ]
                              .filter(Boolean)
                              .join(" ") || "—"}
                          </span>
                        </div>
                        {(performerData.email || performerData.phone) && (
                          <>
                            {performerData.email && (
                              <div className="requisite-item">
                                <span className="requisite-label">Email:</span>
                                <a
                                  href={`mailto:${performerData.email}`}
                                  className="requisite-value requisite-link"
                                >
                                  {performerData.email}
                                </a>
                              </div>
                            )}
                            {performerData.phone && (
                              <div className="requisite-item">
                                <span className="requisite-label">Телефон:</span>
                                <span className="requisite-value">
                                  {performerData.phone}
                                </span>
                              </div>
                            )}
                          </>
                        )}
                      </>
                    ) : isCounterpartyPerformer ? (
                      <>
                        <div className="requisite-item">
                          <span className="requisite-label">БИН/ИИН:</span>
                          <span className="requisite-value">
                            {performerData.bin_or_inn || "—"}
                          </span>
                        </div>
                        <div className="requisite-item">
                          <span className="requisite-label">Адрес:</span>
                          <span className="requisite-value">
                            {formatCounterpartyAddress(performerData)}
                          </span>
                        </div>
                        <div className="requisite-item">
                          <span className="requisite-label">Контактное лицо:</span>
                          <span className="requisite-value">
                            {performerData.type === "legal_entity"
                              ? performerData.full_name_first_supervisor
                                ? performerData.job_title
                                  ? `${performerData.full_name_first_supervisor} (${performerData.job_title})`
                                  : `${performerData.full_name_first_supervisor} (директор)`
                                : "—"
                              : performerData.full_name || "—"}
                          </span>
                        </div>
                        <div className="requisite-item">
                          <span className="requisite-label">Телефон:</span>
                          <span className="requisite-value">
                            {performerData.phone || "—"}
                          </span>
                        </div>
                        <div className="requisite-item">
                          <span className="requisite-label">Email:</span>
                          <span className="requisite-value">
                            {performerData.email ? (
                              <a
                                href={`mailto:${performerData.email}`}
                                className="requisite-value requisite-link"
                              >
                                {performerData.email}
                              </a>
                            ) : (
                              "—"
                            )}
                          </span>
                        </div>
                      </>
                    ) : null}
                  </div>
                </div>
              )}
            </div>

            <div className="add-agreement-actions">
              <button
                type="button"
                className="btn-next"
                onClick={handleNext}
                disabled={!selectedPerformer}
              >
                Далее
              </button>
            </div>
          </div>
        )}

        {currentStep === 2 && (
          <div className="add-agreement-content">
            {loadingEstimate ? (
              <div className="loading-message">Загрузка данных сметы...</div>
            ) : (
              <>
                <div className="agreement-info-display">
                  <span className="agreement-info-text">
                    Соглашение {agreementNumber} от{" "}
                    {agreementDate
                      ? agreementDate.toLocaleDateString("ru-RU", {
                          day: "2-digit",
                          month: "2-digit",
                          year: "numeric",
                        })
                      : ""}
                  </span>
                </div>

                <h3 className="section-title">Состав работ и стоимость</h3>

                {/* Выбор разделов */}
                <div className="sections-selection-section">
                  <h4 className="subsection-title">
                    Выбор разделов для субподряда
                  </h4>
                  <p className="subsection-description">
                    Отметьте разделы, которые будут выполняться субподрядчиком
                  </p>
                  <div className="sections-grid">
                    {estimateData &&
                      [
                        ...(estimateData.construction_design || []),
                        ...(estimateData.construction_design_project || []),
                      ]
                        .filter(
                          (s) =>
                            s.section?.name?.toLowerCase() !== "инсоляция" &&
                            s.section?.name?.toLowerCase() !== "гп"
                        )
                        .map((section) => {
                          const sectionId = section.section.id;
                          const isSelected = selectedSections.has(sectionId);
                          const totalCost =
                            section.price_with_payroll_taxes ||
                            (section.money_expenditures?.threshold || 0) +
                              (section.money_expenditures?.direct || 0);

                          return (
                            <div
                              key={sectionId}
                              className={`section-card ${
                                isSelected ? "selected" : ""
                              }`}
                              onClick={() => {
                                const newSelected = new Set(selectedSections);
                                if (isSelected) {
                                  newSelected.delete(sectionId);
                                  // Удаляем выбранные блоки для этого раздела
                                  const newSelectedBlocks = {
                                    ...selectedBlocks,
                                  };
                                  delete newSelectedBlocks[sectionId];
                                  setSelectedBlocks(newSelectedBlocks);
                                  const newAgreementCosts = {
                                    ...agreementCosts,
                                  };
                                  delete newAgreementCosts[sectionId];
                                  setAgreementCosts(newAgreementCosts);
                                } else {
                                  newSelected.add(sectionId);
                                }
                                setSelectedSections(newSelected);
                              }}
                            >
                              <input
                                type="checkbox"
                                checked={isSelected}
                                onChange={() => {}}
                                className="section-checkbox"
                              />
                              <div className="section-card-content">
                                <div className="section-code">
                                  {section.section.name}
                                </div>
                                <div className="section-type">
                                  Рабочий проект
                                </div>
                                <div className="section-cost">
                                  {totalCost.toLocaleString("ru-RU")} 〒
                                </div>
                              </div>
                            </div>
                          );
                        })}
                  </div>
                </div>

                {/* Настройка блоков и стоимости */}
                {Array.from(selectedSections).length > 0 && (
                  <div className="blocks-configuration-section">
                    <h4 className="subsection-title">
                      Настройка блоков и стоимости
                    </h4>
                    <p className="subsection-description">
                      Укажите, какие блоки и по какой цене будут выполнены в
                      рамках соглашения
                    </p>
                    {Array.from(selectedSections).map((sectionId) => {
                      const section = sectionsData[sectionId];
                      if (!section) return null;

                      const blocks = section.block_expenditures || [];
                      const sectionBlocks = selectedBlocks[sectionId] || {};
                      const sectionCosts = agreementCosts[sectionId] || {};

                      // Получаем блоки из финансового плана (нужно будет получить из API)
                      // Пока используем данные из block_expenditures
                      const blockNames = [
                        "Паркинг",
                        "Блок 1 (9 эт.)",
                        "Блок 2 (9 эт.)",
                        "Блок 3 (9 эт.)",
                        "Блок 4 (9 эт.)",
                        "Блок 5 (9 эт.)",
                      ];

                      const handleBlockToggle = (blockIndex) => {
                        const newSelectedBlocks = {
                          ...selectedBlocks,
                          [sectionId]: {
                            ...sectionBlocks,
                            [blockIndex]: !sectionBlocks[blockIndex],
                          },
                        };
                        setSelectedBlocks(newSelectedBlocks);

                        // Инициализируем стоимость по плану, если блок выбран
                        if (!sectionBlocks[blockIndex]) {
                          const block = blocks[blockIndex];
                          const planCost =
                            block?.money_expenditures ||
                            section.money_expenditures?.direct /
                              blocks.length ||
                            0;
                          setAgreementCosts({
                            ...agreementCosts,
                            [sectionId]: {
                              ...sectionCosts,
                              [blockIndex]: planCost,
                            },
                          });
                        } else {
                          // Удаляем стоимость при снятии выбора
                          const newCosts = { ...sectionCosts };
                          delete newCosts[blockIndex];
                          setAgreementCosts({
                            ...agreementCosts,
                            [sectionId]: newCosts,
                          });
                        }
                      };

                      const handleCostChange = (blockIndex, value) => {
                        setAgreementCosts({
                          ...agreementCosts,
                          [sectionId]: {
                            ...sectionCosts,
                            [blockIndex]: parseFloat(value) || 0,
                          },
                        });
                      };

                      // Подсчет итогов для раздела
                      const planTotal = Object.keys(sectionCosts).reduce(
                        (sum, blockIndex) => {
                          const block = blocks[parseInt(blockIndex)];
                          return (
                            sum +
                            (block?.money_expenditures ||
                              section.money_expenditures?.direct /
                                blocks.length ||
                              0)
                          );
                        },
                        0
                      );

                      const agreementTotal = Object.values(sectionCosts).reduce(
                        (sum, cost) => sum + (cost || 0),
                        0
                      );

                      return (
                        <div key={sectionId} className="section-blocks-config">
                          <div className="section-blocks-header">
                            <h5 className="section-blocks-title">
                              {section.section.name} Рабочий проект
                            </h5>
                            <button
                              className="remove-section-btn"
                              onClick={() => {
                                const newSelected = new Set(selectedSections);
                                newSelected.delete(sectionId);
                                setSelectedSections(newSelected);
                                const newSelectedBlocks = { ...selectedBlocks };
                                delete newSelectedBlocks[sectionId];
                                setSelectedBlocks(newSelectedBlocks);
                                const newAgreementCosts = {
                                  ...agreementCosts,
                                };
                                delete newAgreementCosts[sectionId];
                                setAgreementCosts(newAgreementCosts);
                              }}
                            >
                              ×
                            </button>
                          </div>
                          <p className="section-blocks-subtitle">
                            Блоки раздела из финансового плана
                          </p>
                          <div className="blocks-table-container">
                            <table className="blocks-table">
                              <thead>
                                <tr>
                                  <th></th>
                                  {blockNames.map((name, idx) => (
                                    <th key={idx}>{name}</th>
                                  ))}
                                </tr>
                              </thead>
                              <tbody>
                                <tr>
                                  <td>Выбрать в соглашение</td>
                                  {blockNames.map((_, blockIndex) => {
                                    const isBlockSelected =
                                      sectionBlocks[blockIndex];
                                    return (
                                      <td key={blockIndex}>
                                        <input
                                          type="checkbox"
                                          checked={isBlockSelected || false}
                                          onChange={() =>
                                            handleBlockToggle(blockIndex)
                                          }
                                        />
                                      </td>
                                    );
                                  })}
                                </tr>
                                <tr>
                                  <td>Стоимость по плану</td>
                                  {blockNames.map((_, blockIndex) => {
                                    const block = blocks[blockIndex];
                                    const planCost =
                                      block?.money_expenditures ||
                                      section.money_expenditures?.direct /
                                        blocks.length ||
                                      0;
                                    return (
                                      <td key={blockIndex}>
                                        {planCost.toLocaleString("ru-RU")} 〒
                                      </td>
                                    );
                                  })}
                                </tr>
                                <tr>
                                  <td>Стоимость по соглашению</td>
                                  {blockNames.map((_, blockIndex) => {
                                    const isBlockSelected =
                                      sectionBlocks[blockIndex];
                                    const cost = sectionCosts[blockIndex] || 0;
                                    return (
                                      <td key={blockIndex}>
                                        {isBlockSelected ? (
                                          <div className="cost-input-wrapper">
                                            <input
                                              type="number"
                                              value={cost}
                                              onChange={(e) =>
                                                handleCostChange(
                                                  blockIndex,
                                                  e.target.value
                                                )
                                              }
                                              className="cost-input"
                                            />
                                            <span className="cost-currency">
                                              〒
                                            </span>
                                          </div>
                                        ) : (
                                          "—"
                                        )}
                                      </td>
                                    );
                                  })}
                                </tr>
                              </tbody>
                            </table>
                          </div>
                          <div className="add-agreement-section-total">
                            <div className="add-agreement-section-total-item">
                              <span>По плану:</span>
                              <span>
                                {planTotal.toLocaleString("ru-RU")} 〒
                              </span>
                            </div>
                            <div className="add-agreement-section-total-item">
                              <span>По соглашению:</span>
                              <span>
                                {agreementTotal.toLocaleString("ru-RU")} 〒
                              </span>
                            </div>
                          </div>
                        </div>
                      );
                    })}

                    {/* Общий итог */}
                    <div className="agreement-total">
                      <div className="agreement-total-label">
                        ИТОГО ПО СОГЛАШЕНИЮ
                      </div>
                      <div className="agreement-total-items">
                        <div className="agreement-total-item">
                          <span>По плану по выбранным блокам:</span>
                          <span>
                            {Array.from(selectedSections)
                              .reduce((sum, sectionId) => {
                                const sectionCosts =
                                  agreementCosts[sectionId] || {};
                                const section = sectionsData[sectionId];
                                const blocks =
                                  section?.block_expenditures || [];
                                return (
                                  sum +
                                  Object.keys(sectionCosts).reduce(
                                    (sectionSum, blockIndex) => {
                                      const block =
                                        blocks[parseInt(blockIndex)];
                                      return (
                                        sectionSum +
                                        (block?.money_expenditures ||
                                          section.money_expenditures?.direct /
                                            blocks.length ||
                                          0)
                                      );
                                    },
                                    0
                                  )
                                );
                              }, 0)
                              .toLocaleString("ru-RU")}{" "}
                            〒
                          </span>
                        </div>
                        <div className="agreement-total-item">
                          <span>По соглашению:</span>
                          <span>
                            {Array.from(selectedSections)
                              .reduce((sum, sectionId) => {
                                const sectionCosts =
                                  agreementCosts[sectionId] || {};
                                return (
                                  sum +
                                  Object.values(sectionCosts).reduce(
                                    (sectionSum, cost) =>
                                      sectionSum + (cost || 0),
                                    0
                                  )
                                );
                              }, 0)
                              .toLocaleString("ru-RU")}{" "}
                            〒
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                <div className="add-agreement-actions">
                  <button
                    type="button"
                    className="btn-back"
                    onClick={() => handleStepClick(1)}
                  >
                    Назад
                  </button>
                  <button
                    type="button"
                    className="btn-next"
                    onClick={handleNext}
                    disabled={selectedSections.size === 0}
                  >
                    Далее
                  </button>
                </div>
              </>
            )}
          </div>
        )}

        {currentStep === 3 && (
          <div className="add-agreement-content add-agreement-step3">
            <div className="agreement-info-display agreement-info-display--step3">
              <span className="agreement-info-text">
                Соглашение {agreementNumber} от:{" "}
                {agreementDate?.toLocaleDateString("ru-RU", {
                  day: "2-digit",
                  month: "2-digit",
                  year: "numeric",
                })}
              </span>
            </div>

            <h3 className="section-title">Сроки и порядок оплаты</h3>

            {/* Сроки выполнения */}
            <div className="form-group">
              <h4 className="subsection-title">Сроки выполнения</h4>
              <p className="subsection-description">Выберите срок выполнения*</p>
              <div className="execution-dates-row">
                <div className="date-field-group">
                  <label className="date-field-label">с:</label>
                  <DatePicker
                    selected={executionStartDate}
                    onChange={(date) => setExecutionStartDate(date)}
                    dateFormat="dd.MM.yyyy"
                    locale={ru}
                    className="date-field-input"
                  />
                </div>
                <div className="date-field-group">
                  <label className="date-field-label">по:</label>
                  <DatePicker
                    selected={executionEndDate}
                    onChange={(date) => setExecutionEndDate(date)}
                    dateFormat="dd.MM.yyyy"
                    locale={ru}
                    className="date-field-input"
                    minDate={executionStartDate}
                  />
                </div>
              </div>
            </div>

            {/* Порядок оплаты */}
            <div className="form-group">
              <h4 className="subsection-title">Порядок оплаты</h4>
              <p className="subsection-description">
                Выберите шаблон графика оплат
              </p>
              <div className="form-group">
                <label className="form-label">Шаблон графика оплат</label>
                <Select
                  styles={selectStyles}
                  options={PAYMENT_TEMPLATES}
                  value={PAYMENT_TEMPLATES.find((t) => t.value === paymentTemplate)}
                  onChange={(opt) => setPaymentTemplate(opt?.value || "30_50_20")}
                  placeholder="Выберите шаблон"
                />
              </div>
            </div>

            {/* Этапы оплаты (30/50/20) */}
            {paymentTemplate === "30_50_20" && selectedTemplateStages.length > 0 && (
              <div className="payment-stages-section">
                <h4 className="subsection-title">Этапы оплаты</h4>
                {selectedTemplateStages.map((stage) => {
                  const amount = Math.round((totalAgreementCost * stage.percent) / 100);
                  return (
                    <div key={stage.key} className="payment-stage-card">
                      <div className="payment-stage-header">
                        <span className="payment-stage-label">
                          {stage.label}: {stage.percent}%
                        </span>
                        <span className="payment-stage-amount">
                          {amount.toLocaleString("ru-RU")} 〒
                        </span>
                      </div>
                      <p className="payment-stage-description">{stage.description}</p>
                      <p className="payment-stage-terms">{stage.terms}</p>
                    </div>
                  );
                })}
                <div className="payment-stage-total">
                  <span className="payment-stage-total-label">Итого:</span>
                  <span className="payment-stage-total-value">
                    {totalAgreementCost.toLocaleString("ru-RU")} 〒
                  </span>
                </div>
              </div>
            )}

            {/* Предпросмотр соглашения */}
            <div className="agreement-preview-section">
              <h4 className="subsection-title">Предпросмотр соглашения</h4>
              <div className="agreement-preview-card">
                <div className="agreement-preview-row">
                  <span className="agreement-preview-label">Соглашение:</span>
                  <span className="agreement-preview-value">{agreementNumber}</span>
                </div>
                <div className="agreement-preview-row">
                  <span className="agreement-preview-label">Дата:</span>
                  <span className="agreement-preview-value">
                    {agreementDate?.toLocaleDateString("ru-RU", {
                      day: "2-digit",
                      month: "2-digit",
                      year: "numeric",
                    })}
                  </span>
                </div>
                <div className="agreement-preview-row">
                  <span className="agreement-preview-label">Стороны:</span>
                  <span className="agreement-preview-value">
                    {GENPROEKT_NAME} и {getSubcontractorName()} (Субподрядчик)
                  </span>
                </div>
                <div className="agreement-preview-row">
                  <span className="agreement-preview-label">Предмет:</span>
                  <span className="agreement-preview-value">
                    {getAgreementSubject()}
                  </span>
                </div>
                <div className="agreement-preview-row">
                  <span className="agreement-preview-label">Стоимость:</span>
                  <span className="agreement-preview-value">
                    {totalAgreementCost.toLocaleString("ru-RU")} 〒 (без НДС)
                  </span>
                </div>
                <div className="agreement-preview-row">
                  <span className="agreement-preview-label">Срок выполнения:</span>
                  <span className="agreement-preview-value">
                    {formatDateRange(executionStartDate, executionEndDate)}
                  </span>
                </div>
                <div className="agreement-preview-row">
                  <span className="agreement-preview-label">Порядок оплаты:</span>
                  <span className="agreement-preview-value">
                    {PAYMENT_TEMPLATES.find((t) => t.value === paymentTemplate)?.label || "—"}
                  </span>
                </div>
                {/* Таблица графика оплат */}
                {paymentTemplate === "30_50_20" && selectedTemplateStages.length > 0 && (
                  <div className="agreement-preview-table-wrap">
                    <table className="agreement-preview-table">
                      <thead>
                        <tr>
                          <th>Этап</th>
                          <th>%</th>
                          <th>План Дата</th>
                          <th>План Сумма</th>
                        </tr>
                      </thead>
                      <tbody>
                        {selectedTemplateStages.map((stage) => {
                          const planDate =
                            paymentPlanDates[stage.key] ?? executionStartDate ?? new Date();
                          return (
                            <tr key={stage.key}>
                              <td>{stage.label}</td>
                              <td>{stage.percent}%</td>
                              <td className="agreement-preview-date-cell">
                                <DatePicker
                                  selected={planDate instanceof Date ? planDate : new Date(planDate)}
                                  onChange={(date) =>
                                    setPaymentPlanDates((prev) => ({
                                      ...prev,
                                      [stage.key]: date,
                                    }))
                                  }
                                  dateFormat="dd.MM.yyyy"
                                  locale={ru}
                                  className="agreement-preview-date-input"
                                />
                              </td>
                              <td>
                                {Math.round(
                                  (totalAgreementCost * stage.percent) / 100
                                ).toLocaleString("ru-RU")}
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            </div>

            {saveError && (
              <div className="add-agreement-submit-error add-agreement-submit-error--step3">
                {saveError}
              </div>
            )}
            <div className="add-agreement-actions add-agreement-actions--step3">
              <button
                type="button"
                className="btn-back"
                onClick={() => handleStepClick(2)}
              >
                Назад
              </button>
              <button type="button" className="btn-cancel-text" onClick={onClose}>
                Отмена
              </button>
              <div className="add-agreement-actions-right">
                <button
                  type="button"
                  className="btn-primary btn-send"
                  disabled={!executionStartDate || !executionEndDate}
                >
                  Отправить на подпись
                </button>
                <button
                  type="button"
                  className="btn-secondary"
                  onClick={handleSave}
                  disabled={savingAgreement || !projectId}
                >
                  {savingAgreement ? "Сохранение…" : "Сохранить"}
                </button>
                <button type="button" className="btn-secondary">
                  Печать PDF
                </button>
              </div>
            </div>
          </div>
        )}
      </div>

      <AddCounterpartyModal
        open={showAddSubcontractorModal}
        onClose={() => setShowAddSubcontractorModal(false)}
        defaultType="counterparty"
        onCounterpartyCreated={(newCounterparty) => {
          setExtraSubcontractors((prev) => [...prev, newCounterparty]);
          setSelectedPerformer({ _source: "counterparty", ...newCounterparty });
          setShowAddSubcontractorModal(false);
        }}
      />
    </div>
  );
};

export default AddAgreement;
